import type { CSSProperties } from 'react';
import type { MemberSpotlightListKey } from '@act/shared/data-transfer-objects';
import { accent, body, muted } from './tokens';
import { MemberSpotlightEditableText } from './MemberSpotlightEditableText';

const listStyle: CSSProperties = {
  color: muted,
  fontFamily: body,
  fontSize: 15,
  lineHeight: 1.5,
};

export interface MemberSpotlightBulletListProps {
  /** Bullet strings for this group. */
  items: string[];
  /** Snapshot list key used for stable ids and labels. */
  listKey: MemberSpotlightListKey;
  /** When true, items and add/remove controls are editable. */
  editable: boolean;
  /**
   * @param index - Item index.
   * @param value - Updated text while typing.
   */
  onItemChange: (index: number, value: string) => void;
  /**
   * @param index - Item index.
   * @param value - Final text after blur.
   */
  onItemBlur: (index: number, value: string) => void;
  /** Appends an empty item. */
  onAdd: () => void;
  /**
   * @param index - Item index to remove.
   */
  onRemove: (index: number) => void;
}

/**
 * Member Spotlight bullet list with optional builder add/remove controls.
 * @param props - Items, edit handlers, and list identity.
 */
export const MemberSpotlightBulletList = ({
  items,
  listKey,
  editable,
  onItemChange,
  onItemBlur,
  onAdd,
  onRemove,
}: MemberSpotlightBulletListProps) => (
  <div>
    <ul className="space-y-1.5" style={listStyle}>
      {items.map((item, index) => (
        <li key={`${listKey}-${index}`} className="flex gap-3">
          <span
            className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: accent }}
            aria-hidden="true"
          />
          <MemberSpotlightEditableText
            as="span"
            value={item}
            editable={editable}
            onChange={value => onItemChange(index, value)}
            onBlur={value => onItemBlur(index, value)}
          />
          {editable && items.length > 1 ? (
            <button
              type="button"
              className="shrink-0 text-xs text-[color:var(--midnight-75)]"
              aria-label={`Remove ${listKey} item ${index + 1}`}
              onClick={() => onRemove(index)}
            >
              ×
            </button>
          ) : null}
        </li>
      ))}
    </ul>
    {editable ? (
      <button
        type="button"
        className="mt-2 text-xs font-medium text-[color:var(--reema-coral-100)]"
        onClick={onAdd}
      >
        Add item
      </button>
    ) : null}
  </div>
);
